import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { ApiError } from "../api";
import { useDialogFokus } from "../lib/dialog";
import { fehlerText as toastFehlerText, toast } from "./ui/Toast";

export { toast } from "./ui/Toast";
export { bestaetigen } from "./ui/Bestaetigen";

/*
 * Gemeinsame Bausteine der Verwaltungsseiten (Belege, Regeln, Einstellungen,
 * Ordner-Dialoge): Dialogverhalten, Rückmeldungen, Zahlen und Daten auf Deutsch.
 */

// ─── Fehlertexte ──────────────────────────────────────────────────────────────

/** Eine Ausnahme in einen Satz übersetzen, den man ohne Entwicklerwissen versteht. */
export function fehlerText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 401) return "Deine Sitzung ist abgelaufen. Bitte neu anmelden.";
    if (e.status === 413) return "Die Datei ist zu groß für den Upload.";
    if (/^\d+$/.test(e.message)) {
      return e.status >= 500
        ? "Der Server hat einen Fehler gemeldet. Bitte später erneut versuchen."
        : `Das hat nicht geklappt (Fehler ${e.message}).`;
    }
    return e.message;
  }
  return toastFehlerText(e);
}

/** Fehler als Toast, mit denselben lesbaren Texten wie oben. */
export function fehlerMelden(e: unknown, vorsatz?: string) {
  toast.fehler(vorsatz ? `${vorsatz}: ${fehlerText(e)}` : fehlerText(e));
}

// ─── Dialogverhalten ─────────────────────────────────────────────────────────

/**
 * Tastaturverhalten eines modalen Dialogs (über den gemeinsamen Dialogstapel):
 * Esc schließt, sofern gerade erlaubt; Tab bleibt im Dialog; der Fokus springt
 * hinein und beim Schließen zurück.
 */
export function useDialog(ref: RefObject<HTMLElement | null>, onClose: () => void, schliessbar = true) {
  const darf = useRef(schliessbar);
  darf.current = schliessbar;
  useDialogFokus(ref, () => { if (darf.current) onClose(); });
}

// ─── Rückmeldungen am Ort ────────────────────────────────────────────────────

export type MeldungArt = "ok" | "fehler" | "info";
export interface Meldung { text: string; art: MeldungArt }

/**
 * Eine Rückmeldung, die an ihrer Stelle stehen bleiben soll (etwa ein
 * Testergebnis). Erfolg verschwindet nach ein paar Sekunden, Fehler bleiben.
 */
export function useMeldung(dauer = 4000) {
  const [meldung, setMeldung] = useState<Meldung | null>(null);
  const timer = useRef<number | null>(null);

  const melden = useCallback((text: string | null, art: MeldungArt = "ok") => {
    if (timer.current) window.clearTimeout(timer.current);
    setMeldung(text ? { text, art } : null);
    if (text && art !== "fehler") timer.current = window.setTimeout(() => setMeldung(null), dauer);
  }, [dauer]);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  return [meldung, melden] as const;
}

export function MeldungZeile({ meldung, onClose }: { meldung: Meldung | null; onClose?: () => void }) {
  if (!meldung) return null;
  const Icon = meldung.art === "fehler" ? AlertTriangle : CheckCircle2;
  return (
    <div className="v-meldung" data-art={meldung.art} role={meldung.art === "fehler" ? "alert" : "status"}>
      <Icon aria-hidden="true" />
      <span>{meldung.text}</span>
      {onClose && (
        <button type="button" className="v-meldung-zu" onClick={onClose} aria-label="Meldung schließen">
          <X aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

// ─── Zahlen und Daten ────────────────────────────────────────────────────────

/** „1.234,56" oder „1234.56" → 1234.56; leer → null; Unsinn → NaN. */
export function zahlLesen(v: string): number | null {
  const s = v.trim().replace(/\s|€|%/g, "");
  if (s === "") return null;
  const norm = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  return /^-?\d*(\.\d+)?$/.test(norm) && norm !== "-" && norm !== "" ? Number(norm) : Number.NaN;
}

/** Zahl fürs Eingabefeld: deutsches Komma, ohne Tausenderpunkte. */
export function zahlFeld(n: number | null | undefined, stellen?: number): string {
  if (n === null || n === undefined) return "";
  return stellen === undefined
    ? String(n).replace(".", ",")
    : n.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen, useGrouping: false });
}

export function zahlDe(n: number, stellen = 2): string {
  return n.toLocaleString("de-DE", { minimumFractionDigits: stellen, maximumFractionDigits: stellen });
}

/** „2026-09-28" oder ISO-Zeitstempel → „28.09.2026". Ohne Zeitzonenversatz. */
export function datumDe(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE");
}

// ─── Kostenschätzung ─────────────────────────────────────────────────────────

/**
 * Listenpreise je Million Tokens (Eingabe/Ausgabe, USD) als Richtwert für
 * Schätzungen — dieselben Muster wie packages/core/src/kosten.ts, Claude-Preise
 * Stand 09/2026. Erkannt wird über Teilstücke des Modellnamens (Langdock-IDs
 * wie „claude-sonnet-4-6-default", Azure-Bereitstellungen mit Modellnamen).
 */
const USD_ZU_EUR = 0.92;
const PREISE: [RegExp, number, number][] = [
  [/fable|mythos/i, 10, 50],
  [/opus-5-5/i, 4, 20],
  [/opus-4-[5-9]|opus-5/i, 5, 25],
  [/opus/i, 15, 75],
  [/haiku-4|haiku-5/i, 1, 5],
  [/haiku/i, 0.8, 4],
  [/sonnet-5/i, 2, 10],
  [/sonnet/i, 3, 15],
  [/gpt-5-nano/i, 0.05, 0.4],
  [/gpt-5-mini/i, 0.25, 2],
  [/gpt-5/i, 1.25, 10],
  [/gpt-4\.1-nano/i, 0.1, 0.4],
  [/gpt-4\.1-mini/i, 0.4, 1.6],
  [/gpt-4\.1/i, 2, 8],
  [/gpt-4o-mini/i, 0.15, 0.6],
  [/gpt-4o/i, 2.5, 10],
  [/o4-mini|o3-mini/i, 1.1, 4.4],
  [/mistral-large/i, 2, 6],
  [/mistral-medium/i, 0.4, 2],
  [/mistral-small|ministral/i, 0.1, 0.3],
];

/** Euro je Million Eingabe-/Ausgabe-Tokens für ein Modell; null, wenn unbekannt. */
export function preisFuer(model: string | null | undefined): { ein: number; aus: number } | null {
  const t = model ? PREISE.find(([m]) => m.test(model)) : undefined;
  return t ? { ein: t[1] * USD_ZU_EUR, aus: t[2] * USD_ZU_EUR } : null;
}
